// src/PrivacyPolicy.jsx

const PrivacyPolicy = ({ onBack }) => {
  const lastUpdated = "November 1, 2026";

  return (
    <section style={{
      padding: "clamp(64px, 10vh, 120px) 24px 80px",
      maxWidth: "720px", margin: "0 auto",
    }}>
      <button
        onClick={onBack}
        aria-label="Go back"
        style={{
          background: "none", border: "none", cursor: "pointer",
          fontSize: "14px", color: "var(--ink-muted)", fontFamily: "inherit",
          marginBottom: "32px", padding: 0,
        }}
        onMouseEnter={(e) => e.target.style.color = "var(--ink)"}
        onMouseLeave={(e) => e.target.style.color = "var(--ink-muted)"}
      >
        &larr; Back
      </button>

      <h1 style={{
        fontFamily: "'DM Serif Display', serif",
        fontSize: "clamp(28px, 4vw, 36px)", fontWeight: "400",
        color: "var(--ink)", letterSpacing: "-0.02em",
        marginBottom: "8px",
      }}>
        Privacy Policy
      </h1>
      <p style={{
        fontSize: "13px", color: "var(--ink-muted)", marginBottom: "40px",
      }}>
        Last updated: {lastUpdated}
      </p>

      <div style={{
        fontSize: "15px", lineHeight: 1.8, color: "var(--ink-soft)",
        display: "flex", flexDirection: "column", gap: "28px",
      }}>
        <div>
          <h2 style={sh}>Overview</h2>
          <p>
            Ask Irene ("we," "us," or "our") operates the website askirene.ai. This
            Privacy Policy explains what information we collect, how we use it, and
            what choices you have. We are committed to protecting your privacy and
            handling your data with care.
          </p>
        </div>

        <div>
          <h2 style={sh}>Information We Collect</h2>
          <p style={{ marginBottom: "12px" }}>
            <strong style={st}>Account information:</strong> When you sign in with Google,
            we receive your name, email address, and profile photo from Google. This
            information is stored securely in our database (hosted by Supabase) and
            is used solely to manage your account.
          </p>
          <p style={{ marginBottom: "12px" }}>
            <strong style={st}>Conversation data:</strong> If you are a paid (Pro)
            subscriber, your conversations with Irene are stored so you can access
            them later. Stored conversations are retained until you delete them or
            cancel your account. Free users' conversations are not stored and exist
            only during the active browser session.
          </p>
          <p style={{ marginBottom: "12px" }}>
            <strong style={st}>Payment information:</strong> Payments are processed
            entirely by Stripe. We never see, store, or have access to your credit
            card number or bank details. We receive only your subscription status
            and email from Stripe.
          </p>
          <p style={{ marginBottom: "12px" }}>
            <strong style={st}>Uploaded bills and documents:</strong> If you upload a
            bill, EOB, or other document, it is sent to our AI provider (Anthropic)
            to generate a response. We do not store the file itself; only the file
            name and the conversation are saved, and only for Pro subscribers.
          </p>
          <p style={{ marginBottom: "12px" }}>
            <strong style={st}>Email address (guide signup):</strong> If you request
            the Cancer Cost Survival Guide, we store the email address you enter
            in our database (hosted by Supabase). We may occasionally
            email you helpful updates. Every email includes an unsubscribe link, and
            you can also ask us to remove your address at hello@askirene.ai.
          </p>
          <p>
            <strong style={st}>Usage data:</strong> With your consent, we collect
            basic analytics data such as page views and general usage patterns
            through PostHog (see "Cookies and analytics" below). Analytics are
            off until you accept; no analytics data is collected if you reject or
            have not yet chosen.
          </p>
        </div>

        <div>
          <h2 style={sh}>How We Use Your Information</h2>
          <p>
            We use your information to provide and improve Ask Irene's services.
            Specifically: to authenticate your account, deliver the chat advisor
            experience, store conversation history for Pro subscribers, process
            payments through Stripe, and respond to support requests. We do not use
            your conversations to train AI models. We do not sell, rent, or share
            your personal information with third parties for marketing purposes.
          </p>
        </div>

        <div>
          <h2 style={sh}>Data Storage and Security</h2>
          <p>
            Your data is stored securely using Supabase (PostgreSQL database with
            row-level security) and transmitted over encrypted connections (HTTPS/TLS).
            Conversation data is isolated per user — no one can access another
            user's conversations. While we implement reasonable security measures,
            no method of electronic storage is 100% secure, and we cannot guarantee
            absolute security.
          </p>
        </div>

        <div>
          <h2 style={sh}>Data Retention</h2>
          <p style={{ marginBottom: "12px" }}>
            <strong style={st}>Conversation history:</strong> Pro subscribers'
            conversations and messages are retained until you manually delete them
            or cancel your account. Upon cancellation, your data is deleted within
            30 days.
          </p>
          <p>
            <strong style={st}>Account data:</strong> Your account information
            (name, email, subscription status) is retained for as long as your
            account is active. You may request deletion of your account and all
            associated data at any time by contacting us at hello@askirene.ai.
          </p>
        </div>

        <div>
          <h2 style={sh}>Cookies and analytics</h2>
          <p style={{ marginBottom: "12px" }}>
            <strong style={st}>PostHog analytics:</strong> We use PostHog for
            privacy-friendly product analytics to understand which pages are most
            useful. PostHog never receives your conversations, uploaded bills,
            name, or email address.
          </p>
          <p style={{ marginBottom: "12px" }}>
            <strong style={st}>Consent required:</strong> Analytics are off by
            default. No events are sent and no analytics cookies or storage are
            set until you click "Accept analytics." Choosing "Reject analytics"
            means no events and no analytics cookies — ever. Any PostHog cookies
            or storage set before this policy was in place are deleted when you
            reject.
          </p>
          <p style={{ marginBottom: "12px" }}>
            <strong style={st}>Global Privacy Control:</strong> If your browser
            signals Global Privacy Control (GPC), we treat that as a rejection
            automatically and do not ask again unless you clear the signal.
          </p>
          <p style={{ marginBottom: "12px" }}>
            <strong style={st}>Changing your choice:</strong> You can change your
            analytics preference at any time using the "Cookie settings" link in
            the site footer.
          </p>
          <p style={{ marginBottom: "12px" }}>
            <strong style={st}>Strictly necessary cookies:</strong> Some cookies
            are required for the service to function and are always active
            regardless of your analytics choice: Supabase session cookies (sign-in),
            Stripe cookies (payment processing), and the cookie that stores your
            consent preference.
          </p>
          <p>
            <strong style={st}>Error monitoring:</strong> We use Sentry to capture
            application errors so we can fix bugs quickly. Sentry receives no
            conversation content, uploaded bills, or personal information.
          </p>
        </div>

        <div>
          <h2 style={sh}>Your Rights and Choices</h2>
          <p style={{ marginBottom: "12px" }}>
            <strong style={st}>Delete your conversations:</strong> Pro subscribers can
            delete all saved conversations at any time from the Account page. This
            action is permanent and cannot be undone.
          </p>
          <p style={{ marginBottom: "12px" }}>
            <strong style={st}>Delete your account:</strong> You can sign out at any
            time. To request complete deletion of your account and all associated
            data, contact us at the email below.
          </p>
          <p>
            <strong style={st}>Cancel your subscription:</strong> You can cancel your
            Pro subscription at any time through the Account page. You will retain
            Pro access until the end of your current billing period.
          </p>
        </div>

        <div>
          <h2 style={sh}>Third-Party Services</h2>
          <p>
            Ask Irene uses the following third-party services to operate, each with
            its own privacy policy: Google (authentication), Supabase (database and
            authentication), Stripe (payment processing), Netlify (hosting),
            Anthropic (AI chat functionality), PostHog (analytics — only when
            you have accepted), Sentry (error monitoring), and Google Fonts
            (typefaces; your browser loads them from Google's servers, which see
            your IP address). Your conversations
            are sent to Anthropic's API to generate responses. Anthropic's data
            retention and privacy policies apply to that processing.
          </p>
        </div>

        <div>
          <h2 style={sh}>Health Information</h2>
          <p>
            Ask Irene is not a healthcare provider, health plan, or healthcare
            clearinghouse. We are not a "covered entity" or "business associate"
            under HIPAA. Any health-related information you voluntarily share in
            conversations is treated with the same security measures as all other
            data, but is not subject to HIPAA regulations. We strongly recommend
            that you do not share sensitive identifiers such as Social Security
            numbers, insurance member IDs, or medical record numbers in your
            conversations.
          </p>
        </div>

        <div id="consumer-health-data">
          <h2 style={sh}>Consumer Health Data</h2>
          <p style={{ marginBottom: "12px" }}>
            Some state laws, including Washington's My Health My Data Act and similar
            laws in Nevada and Connecticut, protect "consumer health data." Information
            you share about your medical bills, treatment, diagnoses, or insurance
            claims may fall into this category. This section explains how we handle it.
          </p>
          <p style={{ marginBottom: "12px" }}>
            <strong style={st}>What we collect:</strong> Health-related details you
            choose to type into the chat or include in a document you upload, such as
            services received, dates of service, diagnoses or procedure codes,
            insurance information, and amounts billed.
          </p>
          <p style={{ marginBottom: "12px" }}>
            <strong style={st}>Where it comes from:</strong> Directly from you. We do
            not buy health data or collect it from other sources.
          </p>
          <p style={{ marginBottom: "12px" }}>
            <strong style={st}>How we use it:</strong> Only to provide the service you
            asked for — generating Irene's responses and, for Pro subscribers, saving
            your conversation history. We do not use it for advertising, we do not
            sell it, and we do not use it to train AI models.
          </p>
          <p style={{ marginBottom: "12px" }}>
            <strong style={st}>Who receives it:</strong> Our service providers that
            process it on our behalf to run Ask Irene: Anthropic (AI responses),
            Supabase (database, Pro conversation history only), and Netlify (hosting).
            It is never sent to our analytics (PostHog) or error-monitoring (Sentry)
            tools, and it is not shared with any third party for their own purposes.
          </p>
          <p>
            <strong style={st}>Your rights:</strong> You can ask to confirm whether we
            hold your consumer health data, get a copy of it, or have it deleted, and
            you can withdraw consent at any time. Pro subscribers can delete saved
            conversations instantly from the Account page. For any other request,
            email hello@askirene.ai and we will respond within 45 days. If we deny a
            request, you may appeal by replying to our response.
          </p>
        </div>

        <div>
          <h2 style={sh}>Children's Privacy</h2>
          <p>
            Ask Irene is not directed at children under 13 years of age. We do not
            knowingly collect personal information from children under 13. If you
            believe we have collected information from a child under 13, please
            contact us immediately.
          </p>
        </div>

        <div>
          <h2 style={sh}>AI Accuracy Disclaimer</h2>
          <p>
            Irene is an AI assistant, not a licensed attorney, financial advisor,
            healthcare provider, or insurance professional. The guidance she
            provides is for general informational purposes only and should not be
            treated as legal, financial, or medical advice. AI systems can make
            mistakes — information may be incomplete, outdated, or not applicable
            to your specific situation. Always verify important details directly
            with your healthcare provider, insurance company, or a licensed
            professional before taking action on your medical bills or insurance
            claims.
          </p>
        </div>

        <div>
          <h2 style={sh}>Limitation of Liability</h2>
          <p>
            To the fullest extent permitted by applicable law, Ask Irene and its
            operators shall not be liable for any indirect, incidental, special,
            consequential, or punitive damages — including loss of data, revenue,
            or goodwill — arising out of or in connection with your use of this
            service. Our total liability for any claim arising from your use of
            Ask Irene shall not exceed the amount you paid us in the twelve months
            preceding the claim, or $10, whichever is greater. These limitations
            apply regardless of the theory of liability and even if we have been
            advised of the possibility of such damages.
          </p>
        </div>

        <div>
          <h2 style={sh}>Governing Law</h2>
          <p>
            This Privacy Policy and any disputes arising out of or related to your
            use of Ask Irene are governed by the laws of the State of Ohio, without
            regard to its conflict-of-law provisions. By using Ask Irene, you
            consent to the exclusive jurisdiction of the state and federal courts
            located in Ohio for the resolution of any such disputes.
          </p>
        </div>

        <div>
          <h2 style={sh}>Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify
            users of material changes by updating the "Last updated" date at the
            top of this page. Your continued use of Ask Irene after changes
            constitutes acceptance of the updated policy.
          </p>
        </div>

        <div>
          <h2 style={sh}>Contact</h2>
          <p>
            If you have questions about this Privacy Policy or wish to request
            data deletion, please contact us at{" "}
            <a href="mailto:hello@askirene.ai" style={{
              color: "var(--accent)", textDecoration: "underline",
              textUnderlineOffset: "2px",
            }}>hello@askirene.ai</a>.
          </p>
        </div>
      </div>
    </section>
  );
};

const sh = {
  fontSize: "18px", fontWeight: "600", color: "var(--ink)",
  marginBottom: "8px", fontFamily: "'DM Serif Display', serif",
  letterSpacing: "-0.01em",
};

const st = { color: "var(--ink)", fontWeight: "500" };

export default PrivacyPolicy;
